import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {get as _get} from 'lodash';
import {Button, Header} from 'semantic-ui-react';

import Authorization from '@appComponents/Authorization';
import {DISTRIBUTION_INVOICE_STATUS} from '@constants/resources';
import {SAVED, NOT_SAVED} from '@constants/messages';
import {
    INVOICE_STATUS_NON_INVOICEABLE,
    SECURITY_PRIVILEGES_CREATE,
    SECURITY_PRIVILEGES_UPDATE,
} from '@constants/variables';
import {refetchQueryByName} from '@utils/apollo';
import {convertToInt} from '@utils/helpers';
import {showMessageBox} from '@utils/messageBox';
import {hideModal, showModalConfirmation} from '@utils/modal';

import {
    SET_ALL_BETTING_CONFIRMATION,
    SET_ALL_BETTING_CONFIRMATION_NOTE,
    SET_ALL_BETTING_HEADER,
} from '../constants/messages';

const FORM_QUERY_NAME = 'GetDistributionInvoiceStatusesForForm';
const BUTTON_PRIVILEGES = [SECURITY_PRIVILEGES_CREATE, SECURITY_PRIVILEGES_UPDATE];

export const buildBettingNonInvoiceableRows = (groups = []) => {
    const rows = [];

    groups.forEach((group) => {
        group.rows.forEach(({distribution, is_betting: isBetting}) => {
            if (!isBetting) {
                return;
            }

            const existing = _get(distribution, 'invoice_status', null),
                item = {
                    distribution: convertToInt(distribution.id),
                    invoice_status: INVOICE_STATUS_NON_INVOICEABLE,
                    auto_update: false,
                    inherit_on_bookings: true,
                };

            if (existing?.id) {
                item.id = convertToInt(existing.id);
            }

            rows.push(item);
        });
    });

    return rows;
};

export const persistDistributionInvoiceStatuses = ({
    rows,
    eventId,
    createEntity,
    updateEntity,
    entityLabel,
    messageBoxName,
}) => {
    const event = convertToInt(eventId),
        createRows = rows.filter((row) => !row.id),
        updateRows = rows.filter((row) => row.id),
        requests = [];

    if (createRows.length) {
        requests.push(createEntity({
            variables: {event, invoice_statuses: createRows},
        }));
    }

    if (updateRows.length) {
        requests.push(updateEntity({
            variables: {event, invoice_statuses: updateRows},
        }));
    }

    if (!requests.length) {
        return Promise.resolve();
    }

    return Promise.all(requests)
        .then((response) => {
            refetchQueryByName(FORM_QUERY_NAME);
            showMessageBox(
                messageBoxName.success,
                SAVED(entityLabel),
                null,
                'success'
            );

            return response;
        })
        .catch((error) => {
            showMessageBox(
                messageBoxName.error,
                NOT_SAVED(entityLabel),
                `${error}`,
                'error'
            );
        });
};

export const SetAllBettingNonInvoiceable = ({
    groups,
    submitting = false,
    eventId,
    createEntity,
    updateEntity,
    entityLabel,
    messageBoxName,
}) => {
    const [saving, setSaving] = useState(false);

    if (!groups || 0 === groups.length) {
        return null;
    }

    const saveBettingDistributionsAsNonInvoiceable = () => {
        const rows = buildBettingNonInvoiceableRows(groups);

        if (!rows.length) {
            return Promise.resolve();
        }

        return persistDistributionInvoiceStatuses({
            rows,
            eventId,
            createEntity,
            updateEntity,
            entityLabel,
            messageBoxName,
        });
    };

    const handleSetAllBetting = () => {
        showModalConfirmation({
            header: <Header content={SET_ALL_BETTING_HEADER}/>,
            text: (
                <>
                    <p>
                        {SET_ALL_BETTING_CONFIRMATION}
                    </p>
                    <p>
                        {SET_ALL_BETTING_CONFIRMATION_NOTE}
                    </p>
                </>
            ),
            onYes: () => {
                setSaving(true);

                return saveBettingDistributionsAsNonInvoiceable()
                    .finally(() => {
                        hideModal();
                        setSaving(false);
                    });
            },
            onNo: hideModal,
        });
    };

    return (
        <Authorization
            resources={DISTRIBUTION_INVOICE_STATUS}
            privileges={BUTTON_PRIVILEGES}
        >
            <Button
                type='button'
                size='small'
                content={SET_ALL_BETTING_HEADER}
                disabled={submitting || saving}
                onClick={handleSetAllBetting}
            />
        </Authorization>
    );
};

SetAllBettingNonInvoiceable.propTypes = {
    createEntity: PropTypes.func.isRequired,
    entityLabel: PropTypes.string.isRequired,
    eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    groups: PropTypes.array,
    messageBoxName: PropTypes.shape({
        error: PropTypes.string.isRequired,
        success: PropTypes.string.isRequired,
    }).isRequired,
    submitting: PropTypes.bool,
    updateEntity: PropTypes.func.isRequired,
};
